const DEFAULT_MAX_FRAMES = 8;

const waitForAnimationFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

interface RevealAndFocusCanvasNodeOptions {
  nodeId: string;
  revealNode: () => Promise<boolean>;
  container: HTMLElement | null;
  maxFrames?: number;
  shouldContinue?: () => boolean;
  waitForFrame?: () => Promise<void>;
}

function findVisibleNodeWrapper(container: HTMLElement, nodeId: string): HTMLElement | null {
  const wrapper = Array.from(
    container.querySelectorAll<HTMLElement>('.react-flow__node'),
  ).find(
    (candidate) =>
      candidate.dataset.id === nodeId &&
      candidate.closest('.react-flow') === container,
  );

  if (!wrapper) return null;

  const style = getComputedStyle(wrapper);
  const bounds = wrapper.getBoundingClientRect();
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    return null;
  }

  return wrapper;
}

export async function revealAndFocusCanvasNode({
  nodeId,
  revealNode,
  container,
  maxFrames = DEFAULT_MAX_FRAMES,
  shouldContinue = () => true,
  waitForFrame = waitForAnimationFrame,
}: RevealAndFocusCanvasNodeOptions): Promise<boolean> {
  await revealNode();

  if (!container) return false;

  for (let frame = 0; frame < maxFrames; frame += 1) {
    if (!shouldContinue()) return false;

    await waitForFrame();
    const wrapper = findVisibleNodeWrapper(container, nodeId);
    if (wrapper) {
      wrapper.focus({ preventScroll: true });
      return document.activeElement === wrapper;
    }
  }

  return false;
}
